import type { Route } from '../admin-fetch';
import type { ServerResult } from '../server';
import { ADMIN_API, bodyOf, call, failure, queryOf, seg, toRawResponse, toResponse } from '../respond';

// Feedback, surveys and push notifications.

const FEEDBACK = `${ADMIN_API}/feedback`;
const SURVEYS = `${ADMIN_API}/surveys`;
const PUSH = `${ADMIN_API}/notifications`;

/** `{ ok: true }` on success — the feedback screens read nothing else. */
const okOrError = (result: ServerResult<unknown>) => (result.ok ? Response.json({ ok: true }) : failure(result));

/**
 * The survey editor's shape: `{ data }`, or `{ error: CODE, details }` —
 * it switches on the code (SURVEY_LOCKED, SURVEY_NOT_READY, …) and lists
 * what the details name.
 */
function survey<T>(result: ServerResult<T>, okStatus = 200): Response {
  if (result.ok) return Response.json({ data: result.data }, { status: okStatus });
  return Response.json(
    { error: result.code ?? result.error, details: result.details ?? null },
    { status: result.status },
  );
}

export const engagementRoutes: Route[] = [
  // ── feedback
  ['GET', '/api/feedback', async (req) => toResponse(await call('GET', `${FEEDBACK}${queryOf(req)}`), 'feedback')],
  // The list's quick status change — same validated change as the detail page.
  ['PATCH', '/api/feedback', async (req) => {
    const { feedbackId, status, decision } = await bodyOf<{ feedbackId?: string; status?: string; decision?: string }>(req);
    const result = await call('POST', `${FEEDBACK}/${seg(feedbackId ?? '')}/status`, { status, decision: decision ?? '' });
    return result.ok ? Response.json({ success: true }) : failure(result);
  }],
  ['POST', '/api/feedback/:id/status', async (req, { id }) => {
    // decision: do_now | do_later | wont_do — required by the server when status is "reviewed".
    const { status, decision, message } = await bodyOf<{ status?: string; decision?: string; message?: string }>(req);
    return okOrError(
      await call('POST', `${FEEDBACK}/${seg(id)}/status`, { status: status ?? '', decision: decision ?? '', message: message ?? '' }),
    );
  }],
  ['GET', '/api/feedback/:id/responses', async (_req, { id }) =>
    toResponse(await call('GET', `${FEEDBACK}/${seg(id)}/responses`), 'responses')],
  ['POST', '/api/feedback/:id/responses', async (req, { id }) => {
    const { message } = await bodyOf<{ message?: string }>(req);
    return okOrError(await call('POST', `${FEEDBACK}/${seg(id)}/responses`, { message: message ?? '' }));
  }],

  // ── surveys. Who may change one (owner / admin) is decided by the server.
  ['GET', '/api/surveys', async () => survey(await call('GET', SURVEYS))],
  ['POST', '/api/surveys', async (req) => survey(await call('POST', SURVEYS, await bodyOf(req)), 201)],
  ['POST', '/api/surveys/audience-count', async (req) =>
    survey(await call('POST', `${SURVEYS}/audience-count`, await bodyOf(req)))],
  ['GET', '/api/surveys/:id', async (_req, { id }) => survey(await call('GET', `${SURVEYS}/${seg(id)}`))],
  ['PUT', '/api/surveys/:id', async (req, { id }) =>
    survey(await call('PUT', `${SURVEYS}/${seg(id)}`, await bodyOf(req)))],
  ['DELETE', '/api/surveys/:id', async (_req, { id }) => survey(await call('DELETE', `${SURVEYS}/${seg(id)}`))],
  ['POST', '/api/surveys/:id/publish', async (_req, { id }) =>
    survey(await call('POST', `${SURVEYS}/${seg(id)}/publish`))],
  ['POST', '/api/surveys/:id/status', async (req, { id }) => {
    const { status } = await bodyOf<{ status?: string }>(req);
    return survey(await call('POST', `${SURVEYS}/${seg(id)}/status`, { status }));
  }],
  ['POST', '/api/surveys/:id/reset-tests', async (_req, { id }) =>
    survey(await call('POST', `${SURVEYS}/${seg(id)}/reset-tests`))],
  ['GET', '/api/surveys/:id/report', async (req, { id }) => {
    const page = new URL(req.url).searchParams.get('page') ?? '1';
    const result = await call<{ report: unknown; participants: unknown }>(
      'GET',
      `${SURVEYS}/${seg(id)}/report?page=${seg(page)}`,
    );
    if (!result.ok) return survey(result);
    const { report, participants } = result.data;
    return survey({ ok: true, data: { report, participants } });
  }],

  // ── push notifications. The server validates, picks recipients, logs and pushes.
  ['POST', '/api/notifications', async (req) => toRawResponse(await call('POST', PUSH, await bodyOf(req)))],
  ['GET', '/api/notifications/history', async (req) =>
    toResponse(await call('GET', `${PUSH}/history${queryOf(req)}`), 'broadcasts')],
  ['GET', '/api/notifications/recipients', async (req) =>
    toResponse(await call('GET', `${PUSH}/recipients${queryOf(req)}`), 'users')],
  ['GET', '/api/notifications/scheduled', async () => toResponse(await call('GET', `${PUSH}/scheduled`), 'scheduled')],
  ['DELETE', '/api/notifications/scheduled/:id', async (_req, { id }) =>
    toRawResponse(await call('DELETE', `${PUSH}/scheduled/${seg(id)}`))],
  ['GET', '/api/notifications/segments', async (req) =>
    toRawResponse(await call('GET', `${PUSH}/segments${queryOf(req)}`))],
];
